import { useCallback } from 'react';
import { z } from 'zod';
import { FormFieldsSchema } from '@ninja/utilities';
import { HasEmiratesResponse } from '../../../../../common';
import { prepareFieldError } from '../../../members/members-form/helpers/add-field-error';
import { useEmiratesIdField, useFirstNameField, useLastNameField, useUnifiedNumberField } from '../fields';
import { DocumentsForm, MemberDocumentForm } from '../types';

export const useMemberDetailsFieldsStructure = () => {
	const firstNameField = useFirstNameField();
	const lastNameField = useLastNameField();
	const unifiedNumberField = useUnifiedNumberField();
	const emiratesIdField = useEmiratesIdField();
	const getFieldsStructure = useCallback(
		(memberId: string, hasEmiratesId?: boolean, disableEmirateId?: boolean): FormFieldsSchema<DocumentsForm> =>
			hasEmiratesId
				? [emiratesIdField.getField(memberId, disableEmirateId)]
				: [firstNameField.getField(memberId), lastNameField.getField(memberId), unifiedNumberField.getField(memberId)],
		[firstNameField.getField, lastNameField.getField, unifiedNumberField.getField, emiratesIdField.getField]
	);

	const getValidations = () =>
		z.object({
			emiratesId: emiratesIdField.getValidation(),
			firstName: firstNameField.getValidation(),
			lastName: lastNameField.getValidation(),
			unifiedNumber: unifiedNumberField.getValidation(),
		});

	const validate = (
		memberDocumentFormValue:
			| Pick<MemberDocumentForm, 'firstName' | 'lastName' | 'unifiedNumber' | 'emiratesId' | 'hasEmiratesId'>
			| undefined,
		options: { unifiedNumberUseCount: number; emiratesIdUseCount: number },
		addFieldError: ReturnType<typeof prepareFieldError>,
		isEidFlow?: boolean
	) => {
		if (memberDocumentFormValue?.hasEmiratesId === HasEmiratesResponse.No) {
			firstNameField.validate(memberDocumentFormValue?.firstName, addFieldError);
			lastNameField.validate(memberDocumentFormValue?.lastName, addFieldError);
			unifiedNumberField.validate(memberDocumentFormValue?.unifiedNumber, options.unifiedNumberUseCount, addFieldError);
		} else {
			!isEidFlow &&
				emiratesIdField.validate(memberDocumentFormValue?.emiratesId, options.emiratesIdUseCount, addFieldError);
		}
	};

	return {
		getFieldsStructure,
		getValidations,
		validate,
	};
};
